import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SyndicateNotification {
  id: string;
  user_id: string;
  syndicate_id: string;
  notification_type: "upcoming_race" | "race_result" | "syndicate_update" | "member_joined" | "prize_distribution";
  title: string;
  message: string;
  metadata: any;
  is_read: boolean;
  created_at: string;
  syndicates?: {
    name: string;
    individual_horses: {
      name: string;
    } | null;
  } | null;
}

export const useSyndicateNotifications = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["syndicate-notifications", userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("syndicate_notifications")
        .select(`
          *,
          syndicates!syndicate_notifications_syndicate_id_fkey (
            name,
            individual_horses (
              name
            )
          )
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as SyndicateNotification[];
    },
    enabled: !!userId,
  });

  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("syndicate_notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["syndicate-notifications", userId] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: async () => {
      if (!userId) return;

      const { error } = await supabase
        .from("syndicate_notifications")
        .update({ is_read: true })
        .eq("user_id", userId)
        .eq("is_read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["syndicate-notifications", userId] });
    },
  });

  const deleteNotification = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("syndicate_notifications")
        .delete()
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["syndicate-notifications", userId] });
    },
  });

  const unreadCount = query.data?.filter(n => !n.is_read).length || 0;

  return {
    ...query,
    notifications: query.data || [],
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};
